import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";

import { hexToRGBA } from "../../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  display: flex;
  flex-direction: column;
  
  position: relative;

  padding-left: 0;
  padding-right: 0;
  padding-bottom: 16px;

  ${({ noBottomSpace }) => noBottomSpace && css`
    padding-bottom: 0px;
  `};
  
  ${({ hideDivider }) => hideDivider && css`
    padding-bottom: 0;
  `};
`;

const StyledLabel = styled(Form.Label)`
  display: flex;
  align-items: center;

  font-family: 'Arial';
  font-weight: bold;
  font-size: 14px;
  line-height: calc(1.42857rem * (14 / 16));
  color: #424242;

  span {
    color: #790000;
  }

  a {
    color: #b1b1b1;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #001e5a;
    }
  }

  ${({ emptyLabel }) => emptyLabel && css`
    padding-top: 18.56px;
  `};
`;

const StyledControl = styled(Form.Control)`
  appearance: unset;

  min-height: 34px;
  width: 100%;

  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 4px;
  padding-right: 24px;

  font-family: 'Arial';
  font-size: 14px;
  line-height: 24px;
  color: #424242;

  overflow: hidden; 
  white-space: nowrap; 
  text-overflow: ellipsis;
  
  border: 1px solid ${hexToRGBA('#000000', 0.1)};
  border-radius: 0;
  background: ${hexToRGBA('#000000', 0.03)};

  background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/V2-federal/caret_down.svg)`};
  background-repeat: no-repeat;
  background-origin: content-box;
  background-position-x: right;
  background-position-y: center;

  ${({ isValid }) => isValid && css`
    padding-top: 4px !important;
    padding-right: 0px !important;
    border: 1px solid #198754;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`};
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    padding-top: 4px !important;
    padding-right: 0px !important;
    border: 1px solid #DC3545;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`};
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
  `};

  ${({ isValid, isInvalid }) => !(isValid || isInvalid) && css`
    background-origin: unset!important;
  `};

  &:focus-visible {
    outline: none;
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  &.processing-feedback,
  &.valid-feedback,
  &.invalid-feedback {
    font-size: 13px;
    line-height: calc(1.42857rem * (13 / 16));
  }

  &.processing-feedback {
    color: ${hexToRGBA('#424242', 0.5)};
  }

  &.valid-feedback {
    color: #198754;
  }

  &.invalid-feedback {
    color: #DC3545
  }
`;

function Select({
  id,
  hint,
  label,
  emptyLabel,
  hideDivider,
  placeholder,
  noBottomSpace,
  required,
  validated,
  maxLength,
  defaultInvalidMessage = '',
  isInvalid,
  value = '',
  options = [],
  onChange,
  onMouseEnter,
  onMouseLeave,
  ...restProps
}) {
  const handleChange = useCallback((event) => {
    onChange && onChange(event);
  }, [onChange]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter && onMouseEnter({ label: label || placeholder, hint });
  }, [onMouseEnter, label, placeholder, hint]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
      show: false,
    };

    return props;
  }, [id, hint]);

  const currentValueTitle = useMemo(() => {
    return options.find(option => option?.value === value)?.title;
  }, [value, options])

  const valid = !required && validated && !isInvalid || value && validated && !isInvalid;
  const invalid = isInvalid || (required && !value && validated);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup
        controlId={id}
        hideDivider={hideDivider}
        className={invalid ? 'custom-control-invalid' : ''}
        title={currentValueTitle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeave}
        noBottomSpace={noBottomSpace}
      >
        <StyledLabel emptyLabel={emptyLabel}>
          {label}
          {label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <StyledControl
          as="select"
          className="form-control"
          name={id}
          required={required}
          maxLength={maxLength}
          value={value}
          onChange={handleChange}
          isValid={valid}
          isInvalid={invalid}
          {...restProps}
        >
          <option value="" selected>
            {placeholder}
          </option>
          {options.map(({ title, value }) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </StyledControl>
        <StyledFeedback type="invalid">
          {defaultInvalidMessage}
        </StyledFeedback>
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default Select;
